import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MenuProduct } from '../../../models/menu-product.model';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { EditProductDialogComponent } from './editable-product-dialog.component';
import { MenuProductService } from '../../../services/menu-product/menu-product.service';
import { ConfirmDialogComponent } from '../../../shared/confirm-dialog/confirm-dialog.component';
import { MenuProductChannelsDialogComponent } from '../menu-product-channels-dialog/menu-product-channels-dialog.component';
import { MenuProductOptionsDialogComponent } from '../menu-product-options-dialog/menu-product-options-dialog.component';

@Component({
  selector: 'app-editable-product-list',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, MatTooltipModule, MatDialogModule, MatSlideToggleModule, MatProgressSpinnerModule],
  template: `
    <!-- Mobile cards -->
    <div class="md:hidden space-y-2">
      <div *ngFor="let product of products" class="rounded-md border border-gray-200 bg-white p-2 shadow-sm">
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0 flex-1">
            <p class="text-sm font-semibold text-gray-900 leading-5 line-clamp-3">{{ product.name }}</p>
            <p class="mt-0.5 text-xs text-gray-500 line-clamp-3">{{ product.description }}</p>
          </div>
          <div class="text-right shrink-0">
            <p class="text-xs uppercase text-gray-500 tracking-wide">Precio</p>
            <p class="font-mono text-xs text-gray-900">$ {{ product.sellPrice | number:'1.0-2' }}</p>
          </div>
        </div>
        <div *ngIf="canEdit" class="mt-1 flex justify-end gap-1 items-center">
          <span class="toggle-wrapper">
            <mat-slide-toggle
              color="primary"
              [checked]="getEnabled(product)"
              (change)="onToggleEnabled(product, $event.checked)"
              [disabled]="isPending(getKey(product))"
              aria-label="Habilitar producto"
            ></mat-slide-toggle>
            <span class="overlay" *ngIf="isPending(getKey(product))">
              <mat-progress-spinner mode="indeterminate" diameter="16" strokeWidth="3" color="primary"></mat-progress-spinner>
            </span>
          </span>
          <button mat-icon-button color="accent" (click)="onDuplicate(product); $event.stopPropagation()" matTooltip="Duplicar" aria-label="Duplicar">
            <mat-icon>content_copy</mat-icon>
          </button>
          <button mat-icon-button color="primary" (click)="openEditDialog(product); $event.stopPropagation()" matTooltip="Editar" aria-label="Editar">
            <mat-icon>edit</mat-icon>
          </button>
          <button mat-icon-button color="warn" (click)="onDelete(getKey(product)); $event.stopPropagation()" matTooltip="Eliminar" aria-label="Eliminar">
            <mat-icon>delete</mat-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- Desktop table -->
    <div class="overflow-x-auto hidden md:block">
      <table class="editable-products min-w-full table-fixed divide-y divide-gray-200">
        <colgroup>
          <col style="width:33.3333%" />
          <col style="width:33.3333%" />
          <col style="width:16.6667%" />
          <col style="width:16.6667%" />
        </colgroup>
        <thead class="bg-gray-50">
          <tr>
            <th scope="col" style="width:33.3333%" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Nombre
            </th>
            <th scope="col" style="width:33.3333%" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Descripción
            </th>
            <th scope="col" style="width:16.6667%" class="py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Precio
            </th>
            <th scope="col" style="width:16.6667%" class="py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Acciones
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr *ngFor="let product of products" class="hover:bg-gray-50">
            <td style="width:33.3333%" class="px-6 py-4 whitespace-nowrap">
              <input 
                [(ngModel)]="product.name" 
                [disabled]="!isEditing(getKey(product))"
                class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                placeholder="Nombre del producto"
              />
            </td>
            <td style="width:33.3333%" class="px-6 py-4">
              <input 
                [(ngModel)]="product.description" 
                [disabled]="!isEditing(getKey(product))"
                class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                placeholder="Descripción"
              />
            </td>
            <td style="width:16.6667%" class="px-2 py-4 whitespace-nowrap">
              <div class="relative">
                <div class="pointer-events-none absolute inset-y-0 left-2 flex items-center">
                  <span class="text-gray-500 sm:text-sm">$</span>
                </div>
                <input 
                  type="number" 
                  [(ngModel)]="product.sellPrice" 
                  [disabled]="!isEditing(getKey(product))"
                  min="0"
                  step="0.01"
                  class="block w-full rounded-md border-0 py-1.5 pl-6 pr-2 text-right text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 font-mono"
                  placeholder="0.00"
                />
              </div>
            </td>
            <td style="width:16.6667%" class="px-2 py-4 whitespace-nowrap text-right text-sm font-medium space-x-1">
              <ng-container *ngIf="canEdit">
                <span class="toggle-wrapper mr-1 align-middle">
                  <mat-slide-toggle
                    color="primary"
                    [checked]="getEnabled(product)"
                    (change)="onToggleEnabled(product, $event.checked)"
                    [disabled]="isPending(getKey(product))"
                    aria-label="Habilitar producto"
                  ></mat-slide-toggle>
                  <span class="overlay" *ngIf="isPending(getKey(product))">
                    <mat-progress-spinner mode="indeterminate" diameter="16" strokeWidth="3" color="primary"></mat-progress-spinner>
                  </span>
                </span>
                <button 
                  mat-icon-button
                  color="accent"
                  (click)="onDuplicate(product); $event.stopPropagation()"
                  matTooltip="Duplicar producto"
                  aria-label="Duplicar producto"
                >
                  <mat-icon>content_copy</mat-icon>
                </button>
                <button 
                  *ngIf="!isEditing(getKey(product))"
                  mat-icon-button
                  color="primary"
                  (click)="openEditDialog(product); $event.stopPropagation()"
                  matTooltip="Editar producto"
                  aria-label="Editar producto"
                >
                  <mat-icon>edit</mat-icon>
                </button>
                <button 
                  *ngIf="isEditing(getKey(product))"
                  mat-icon-button
                  color="primary"
                  (click)="saveEdit(product); $event.stopPropagation()"
                  matTooltip="Guardar cambios"
                  aria-label="Guardar cambios"
                >
                  <mat-icon>check</mat-icon>
                </button>
                <button 
                  mat-icon-button
                  color="warn"
                  (click)="onDelete(getKey(product)); $event.stopPropagation()"
                  matTooltip="Eliminar producto"
                  aria-label="Eliminar producto"
                >
                  <mat-icon>delete</mat-icon>
                </button>
              </ng-container>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  `,
  styles: [
    `:host ::ng-deep table.editable-products { table-layout: fixed; width: 100%; }`,
    `:host ::ng-deep table.editable-products thead th:nth-child(1),
      :host ::ng-deep table.editable-products tbody td:nth-child(1) { width: 33.3333%; }`,
    `:host ::ng-deep table.editable-products thead th:nth-child(2),
      :host ::ng-deep table.editable-products tbody td:nth-child(2) { width: 33.3333%; }`,
    `:host ::ng-deep table.editable-products thead th:nth-child(3),
      :host ::ng-deep table.editable-products tbody td:nth-child(3) { width: 16.6667%; }`,
    `:host ::ng-deep table.editable-products thead th:nth-child(4),
      :host ::ng-deep table.editable-products tbody td:nth-child(4) { width: 16.6667%; }`,
    `:host ::ng-deep .spin { animation: rotate360 0.9s linear infinite; display: inline-flex; align-items: center; }`,
    `:host ::ng-deep .toggle-wrapper { position: relative; display: inline-flex; align-items: center; justify-content: center; }`,
    `:host ::ng-deep .toggle-wrapper .overlay { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none; z-index: 2; }`,
    `@keyframes rotate360 { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`,
  ]
})
export class EditableProductListComponent {
  @Input() products: MenuProduct[] = [];
  @Input() contextCategoryId?: string;
  @Input() contextCategoryName?: string;
  // RBAC: when false, user cannot edit/duplicate/delete or open edit dialogs
  @Input() canEdit: boolean = false;
  @Output() update = new EventEmitter<MenuProduct>();
  @Output() delete = new EventEmitter<string>();
  @Output() duplicate = new EventEmitter<MenuProduct>();

  constructor(private readonly dialog: MatDialog, private readonly menuProducts: MenuProductService) { }

  // Editing state: which product keys (id or uuid) are currently being edited
  private readonly editingIds = new Set<string>();

  isEditing(id: string): boolean { return !!id && this.editingIds.has(id); }

  startEdit(product: MenuProduct): void {
    if (!this.canEdit) return;
    const key = this.getKey(product);
    if (key) this.editingIds.add(key);
  }

  openChannelsDialog(product: MenuProduct): void {
    if (!this.canEdit) return;
    const ref = this.dialog.open(MenuProductChannelsDialogComponent, {
      data: { product },
      width: '92vw',
      maxWidth: '520px',
      autoFocus: false,
      restoreFocus: false,
    });
    ref.afterClosed().subscribe(() => {
      // no-op
    });
  }

  openOptionsDialog(product: MenuProduct): void {
    if (!this.canEdit) return;
    const ref = this.dialog.open(MenuProductOptionsDialogComponent, {
      data: { product },
      width: '92vw',
      maxWidth: '560px',
      autoFocus: false,
      restoreFocus: false,
    });
    ref.afterClosed().subscribe(() => {
      // no-op
    });
  }

  saveEdit(product: MenuProduct): void {
    const key = this.getKey(product);
    if (key) {
      this.update.emit({ ...product });
      this.editingIds.delete(key);
    }
  }

  // Mobile: open dialog to edit
  openEditDialog(product: MenuProduct): void {
    if (!this.canEdit) return;
    const ref = this.dialog.open(EditProductDialogComponent, {
      data: {
        product,
        // Provide context fallbacks when product lacks category fields
        categoryUuid: this.contextCategoryId,
        categoryName: this.contextCategoryName
      },
      width: '100%',
      maxWidth: '420px'
    });

    ref.afterClosed().subscribe((edited?: MenuProduct | null) => {
      if (edited) {
        this.update.emit(edited);
      }
    });
  }

  onUpdate(product: MenuProduct): void {
    if (product.name && product.name.trim() !== '') {
      this.update.emit({ ...product });
    } else {
      // Reset to original value if name is empty
      const key = this.getKey(product);
      const originalProduct = this.products.find(p => this.getKey(p) === key);
      if (originalProduct) {
        product.name = originalProduct.name;
      }
    }
  }

  onDelete(productId: string): void {
    if (!this.canEdit) return;
    if (!productId) return;
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Eliminar producto',
        message: '¿Estás seguro de que deseas eliminar este producto?',
        cancelLabel: 'Cancelar',
        confirmLabel: 'Eliminar',
        confirmColor: 'warn'
      }
    });
    ref.afterClosed().subscribe((ok: boolean) => {
      if (ok) this.delete.emit(productId);
    });
  }

  onDuplicate(product: MenuProduct): void {
    if (!this.canEdit) return;
    this.duplicate.emit({ ...product });
  }

  // Enabled helpers for the slide toggle
  getEnabled(p: MenuProduct): boolean {
    const anyP: any = p as any;
    const v = anyP?.enabled ?? anyP?.isEnabled;
    return v === undefined || v === null ? true : !!v;
  }
  getKey(p: MenuProduct): string { return ((p as any)?.id || (p as any)?.uuid || '') as string; }
  // pending state per product id (only affects the small spinner near the toggle)
  private readonly pending = new Set<string>();
  isPending(id?: string): boolean { return !!id && this.pending.has(id); }

  onToggleEnabled(product: MenuProduct, checked: boolean): void {
    const id = (product as any)?.id || (product as any)?.uuid;
    if (!id) {
      // No id, update locally only
      const anyP: any = product as any;
      anyP.enabled = checked; if ('isEnabled' in anyP) anyP.isEnabled = checked;
      return;
    }
    this.pending.add(id);
    // Prepare minimal payload for update; service expects uuid
    const uuid = (product as any)?.uuid || (product as any)?.id;
    const payload: any = { uuid, enabled: checked };
    this.menuProducts.updateProduct(payload).subscribe({
      next: (resp: any) => {
        const enabledFromBackend = (resp?.enabled ?? resp?.isEnabled);
        const finalVal = (enabledFromBackend === undefined || enabledFromBackend === null) ? checked : !!enabledFromBackend;
        const anyP: any = product as any;
        anyP.enabled = finalVal; if ('isEnabled' in anyP) anyP.isEnabled = finalVal;
      },
      error: () => {
        // revert state on error
        const anyP: any = product as any;
        const prev = !checked;
        anyP.enabled = prev; if ('isEnabled' in anyP) anyP.isEnabled = prev;
      },
      complete: () => {
        this.pending.delete(id);
      }
    });
  }


}
